<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { useTranslation } from "i18next-vue";
import { v4 as uuid } from "uuid";
import { useQueryClient } from "@tanstack/vue-query";
import { QueryKey } from "@/stores/queries/query-keys";
import type { FieldObservation } from "@/types/field-observations.types";
import type { AnalysisGroupKind } from "@/utils/plan-analysis/types";
import {
  KIND_BY_OBSERVATION_TYPE,
  OBSERVATION_TYPE_BY_KIND,
} from "@/components/field-observations/observation-type";
import type { ObservationEntityRef } from "@/drawing/managers/ObservationsManager";
import { Facade } from "@/drawing";
import { useFieldObservationForm } from "@/composable/useFieldObservationForm";
import { useObservationsList } from "@/composable/useObservationsList";
import { matchObservationPhotos } from "@/composable/usePhotoMarkerMatching";
import { useUpdateFieldObservation } from "@/stores/queries/field-observations.query";
import { useUpdateDiagnosticImage } from "@/stores/queries/diagnostic-images.query";
import CardField from "../cards/CardField.vue";
import AnalysisKindSelector from "../analysis/AnalysisKindSelector.vue";
import PartPickerLine from "./PartPickerLine.vue";
import ObservationCardPlan from "./ObservationCardPlan.vue";
import ObservationCardPhotos from "./ObservationCardPhotos.vue";
import ObservationCardVoices from "./ObservationCardVoices.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import DRBtn from "@/components/UI/DRBtn.vue";

/**
 * Corps d'une carte d'observation OUVERTE — même mécanique que la carte d'un
 * groupe d'analyse (type, nom, commentaire, partie, entités du plan), plus ce
 * qui est propre à une observation : notes vocales (sous le commentaire),
 * photos, suppression.
 * Chaque modification est enregistrée en direct (`useFieldObservationForm`,
 * partagé avec l'éditeur de l'outil Terrain). Monté seulement quand la carte
 * est ouverte : le fermer libère les médias chargés.
 */

const props = defineProps<{
  observation: FieldObservation;
  /** Entités du plan rattachées à l'observation (réactif). */
  entities: ObservationEntityRef[];
}>();

const { t } = useTranslation();
const list = useObservationsList();
const { mutateAsync: updateObservation } = useUpdateFieldObservation();

const {
  currentObservationId,
  currentObservation,
  selectedType,
  selectedPartId,
  name,
  description,
  existingPhotos,
  existingAudioRecordings,
  isUploadingPhoto,
  isUploadingAudio,
  showDeleteConfirm,
  isDeleting,
  showDeleteObservationConfirm,
  isDeletingObservation,
  refetchObservation,
  uploadPhotos,
  uploadAudioBlob,
  confirmDeletePhoto,
  confirmDeleteAudio,
  handleDelete,
  handleDeleteObservation,
} = useFieldObservationForm({
  diagnosticId: list.diagnosticId,
  observation: props.observation,
  onClose: () => list.close(),
});

// ── Type : mêmes trois pastilles que la carte d'un groupe d'analyse ─────────

const kind = computed<AnalysisGroupKind>({
  get: () => KIND_BY_OBSERVATION_TYPE[selectedType.value],
  set: (value) => {
    selectedType.value = OBSERVATION_TYPE_BY_KIND[value];
  },
});

// ── Rattachement au plan ────────────────────────────────────────────────────

/**
 * localId de l'observation — clé du rattachement plan. Peut manquer sur une
 * observation créée avant ce palier : on en assigne un à la volée (le backend
 * ne l'accepte que si absent, jamais d'écrasement).
 */
const localId = ref<string | null>(
  props.observation.localId ?? currentObservation.value?.localId ?? null,
);

const ensureLocalId = async (): Promise<string | null> => {
  if (localId.value) return localId.value;
  const fromServer = currentObservation.value?.localId;
  if (fromServer) {
    localId.value = fromServer;
    return fromServer;
  }
  if (!currentObservationId.value) return null;
  const generated = uuid();
  await updateObservation({
    id: currentObservationId.value,
    payload: { localId: generated },
  });
  localId.value = generated;
  return generated;
};

/** Sélection courante SANS les éléments déjà rattachés (les seuls à ajouter). */
const unattachedSelection = computed(() => {
  const attached = new Set(props.entities.map((entity) => entity.handle));
  return list.selectionHandles.value.filter((handle) => !attached.has(handle));
});

const stampHandles = async (handles: string[]): Promise<void> => {
  if (!handles.length) return;
  const id = await ensureLocalId();
  if (id) list.stampHandles(handles, id);
};

const addSelected = (): Promise<void> =>
  stampHandles([...unattachedSelection.value]);

// Mode « cliquez sur les éléments à rajouter » : chaque élément touché
// rejoint l'observation, puis la sélection est vidée pour enchaîner (le
// garde-fou évite la boucle sur le clear).
let addingFromSelection = false;
watch(
  () => list.selectionHandles.value,
  async () => {
    if (!list.addModeActive.value || addingFromSelection) return;
    const handles = [...unattachedSelection.value];
    if (!handles.length) return;
    addingFromSelection = true;
    try {
      await stampHandles(handles);
      if (Facade.isInitialized) Facade.selection.clear();
    } finally {
      addingFromSelection = false;
    }
  },
);

const queryClient = useQueryClient();

onUnmounted(() => {
  // Fermer la carte (ou en ouvrir une autre) sort du mode ajout.
  list.addModeActive.value = false;

  // Nom et commentaire sont enregistrés avec un léger différé : la liste peut
  // avoir une sauvegarde de retard. On y reporte la saisie, sinon la carte
  // fermée — et le formulaire si on la rouvre aussitôt — afficherait l'ancien
  // texte, et une frappe repartirait de lui. (Un commentaire vidé n'est pas
  // reporté : le serveur ne l'enregistre pas.)
  const id = props.observation.id;
  queryClient.setQueryData<FieldObservation[]>(
    [QueryKey.FIELD_OBSERVATIONS, { diagnosticId: list.diagnosticId.value }],
    (current) =>
      current?.map((observation) =>
        observation.id === id
          ? {
              ...observation,
              name: name.value.trim(),
              description: description.value || observation.description,
            }
          : observation,
      ),
  );
});

const removeEntity = (handle: string): void => {
  list.detachHandles([handle]);
};

const openMarkerPicker = async (): Promise<void> => {
  const id = await ensureLocalId();
  if (id) list.openMarkerPicker(id);
};

const showClearConfirm = ref(false);
const clearEntities = (): void => {
  if (localId.value) list.clearObservation(localId.value);
  showClearConfirm.value = false;
};

/** Suppression de l'observation : détache aussi ses entités du plan. */
const onDeleteObservationConfirm = async (): Promise<void> => {
  const id = localId.value;
  await handleDeleteObservation();
  // Le composable ne referme la confirmation qu'en cas de succès.
  if (!showDeleteObservationConfirm.value && id) {
    list.clearObservation(id);
  }
};

// ── Rapprochement photo ↔ marqueur ──────────────────────────────────────────
// Le lien est écrit sur le marqueur, qui porte l'id de ses photos ; le reste est
// lu à l'intérieur de l'observation (cf. `matchObservationPhotos`).

const { mutateAsync: updateDiagnosticImage } = useUpdateDiagnosticImage();

const photoMatch = computed(() =>
  matchObservationPhotos(props.entities, currentObservation.value?.photos ?? []),
);

const photoIdsOf = (handle: string): number[] =>
  props.entities.find((entity) => entity.handle === handle)?.photoIds ?? [];

/** Ajoute la photo au marqueur, ou la retire si elle y est déjà. */
const toggleMarkerPhoto = (photoId: number, handle: string): void => {
  const current = photoIdsOf(handle);
  list.setMarkerPhotos(
    handle,
    current.includes(photoId)
      ? current.filter((id) => id !== photoId)
      : [...current, photoId],
  );
};

/**
 * Plus aucun marqueur pour cette photo. Retirer ses liens ne suffit pas si son
 * numéro désigne encore un marqueur (anciennes associations) : il est effacé.
 */
const clearPhotoMarkers = async (photoId: number): Promise<void> => {
  for (const entity of props.entities) {
    if (entity.photoIds.includes(photoId)) {
      list.setMarkerPhotos(
        entity.handle,
        entity.photoIds.filter((id) => id !== photoId),
      );
    }
  }
  const photo = currentObservation.value?.photos?.find((p) => p.id === photoId);
  if (
    photo?.photoNumber != null &&
    props.entities.some((entity) => entity.markerNumber === photo.photoNumber)
  ) {
    await updateDiagnosticImage({ id: photoId, payload: { photoNumber: null } });
    await refetchObservation();
  }
};

const assignPhoto = (photoId: number, handle: string | null): void => {
  if (handle) toggleMarkerPhoto(photoId, handle);
  else void clearPhotoMarkers(photoId);
};

const uploadAndLink = async (
  files: File[],
  markerHandle: string | null,
): Promise<void> => {
  const newIds = await uploadPhotos(files);
  const handle = markerHandle ?? photoMatch.value.soleMarkerHandle;
  if (handle && newIds.length) {
    list.setMarkerPhotos(handle, [...photoIdsOf(handle), ...newIds]);
  }
};
</script>

<template>
  <!-- Les drops hors tuiles sont neutralisés : sans ça le navigateur ouvrirait
       l'image et le dessin en cours serait perdu. -->
  <div
    class="observation-card-editor d-flex flex-column gap-3"
    @dragover.prevent
    @drop.prevent
  >
    <CardField :label="t('canvas:observations.card.kindLabel')">
      <AnalysisKindSelector v-model="kind" />
    </CardField>

    <CardField :label="t('canvas:observations.card.nameLabel')">
      <el-input
        v-model="name"
        :maxlength="200"
        :placeholder="t('canvas:observations.card.namePlaceholder')"
      />
    </CardField>

    <CardField :label="t('canvas:observations.card.commentLabel')">
      <el-input
        v-model="description"
        type="textarea"
        :autosize="{ minRows: 2, maxRows: 8 }"
        :placeholder="t('canvas:observations.editor.notesPlaceholder')"
      />
      <!-- Le vocal est un commentaire dicté : juste sous la saisie -->
      <ObservationCardVoices
        class="mt-2"
        :recordings="existingAudioRecordings"
        :is-uploading="isUploadingAudio"
        :diagnostic-id="list.diagnosticId.value"
        @recorded="uploadAudioBlob"
        @delete="confirmDeleteAudio"
      />
    </CardField>

    <CardField :label="t('dashboard:fieldObservations.part.label')">
      <PartPickerLine
        v-model="selectedPartId"
        :diagnostic-id="list.diagnosticId.value"
      />
    </CardField>

    <ObservationCardPlan
      :entities="entities"
      :photo-states="photoMatch.markerStates"
      :extra-selected-count="unattachedSelection.length"
      :add-mode="list.addModeActive.value"
      @entity-remove="removeEntity"
      @add-selected="addSelected"
      @toggle-add-mode="list.toggleAddMode()"
      @detach-all="showClearConfirm = true"
      @open-marker-picker="openMarkerPicker"
    />

    <ObservationCardPhotos
      :photos="existingPhotos"
      :is-uploading="isUploadingPhoto"
      :choosable-markers="photoMatch.choosableMarkers"
      :markers-by-photo="photoMatch.markersByPhoto"
      :uncovered-markers="photoMatch.uncoveredMarkers"
      @upload="uploadAndLink"
      @delete="confirmDeletePhoto"
      @assign="assignPhoto"
    />

    <!-- Pied : supprimer / fermer -->
    <div
      class="actions-row d-flex justify-content-between gap-2"
      @click.stop
    >
      <DRBtn
        icon="bi-trash"
        :label="t('delete')"
        variant="outline-danger"
        size="sm"
        :disabled="!currentObservationId"
        @click="showDeleteObservationConfirm = true"
      />
      <DRBtn
        icon="bi-check-lg"
        :label="t('close')"
        variant="outline-white"
        size="sm"
        @click="list.close()"
      />
    </div>

    <!-- Confirmations -->
    <ConfirmationModal
      class="canvas-dialog"
      v-model="showDeleteConfirm"
      :title="t('dashboard:fieldObservations.confirmDeleteMedia.title')"
      :message="t('dashboard:fieldObservations.confirmDeleteMedia.message')"
      :confirmLabel="t('delete')"
      :loading="isDeleting"
      danger
      @confirm="handleDelete"
    />
    <ConfirmationModal
      class="canvas-dialog"
      v-model="showDeleteObservationConfirm"
      :title="t('dashboard:fieldObservations.confirmDelete.title')"
      :message="t('dashboard:fieldObservations.confirmDelete.message')"
      :confirmLabel="t('delete')"
      :loading="isDeletingObservation"
      danger
      @confirm="onDeleteObservationConfirm"
    />
    <ConfirmationModal
      class="canvas-dialog"
      v-model="showClearConfirm"
      :title="t('canvas:observations.editor.plan.detachConfirmTitle')"
      :message="t('canvas:observations.editor.plan.detachConfirmMessage')"
      :confirmLabel="t('canvas:observations.editor.plan.detachAll')"
      danger
      @confirm="clearEntities"
    />
  </div>
</template>

<style lang="scss" scoped>
.observation-card-editor {
  cursor: default;
}

.actions-row {
  border-top: 1px solid var(--canvas-border);
  padding-top: 8px;
}
</style>
